import { useCallback, useMemo } from "react";

import { useAppSelector } from "@/redux/hooks";

import {
	useGetPinnedFactsQuery,
	usePinResearchFactMutation,
	useUnpinResearchFactMutation,
} from "./deepResearch.api";
import { selectPinnedFacts } from "./deepResearch.slice";
import { DeepResearchSource, PinnedFact } from "./schema";

export interface PinnableFact {
	factKey: string;
	factLabel?: string;
	factText: string;
	sources?: DeepResearchSource[];
}

interface UsePinnedResearchFactsResult {
	/** The pinned facts themselves, oldest first — what the next sequence will mention. */
	pinnedFacts: PinnedFact[];
	pinnedKeys: Set<string>;
	pinnedCount: number;
	isPinning: boolean;
	togglePin: (fact: PinnableFact) => Promise<void>;
	/** Removes one pin by its row id (the generate panel's chips). */
	unpinFact: (id: number) => Promise<void>;
}

/**
 * Pinning collects facts for the copywriter; it never triggers a generation.
 * The pinned set is read from the slice (bucketed per prospect) so switching
 * leads can never show another lead's pins from a warm cache.
 */
export const usePinnedResearchFacts = (
	prospectId?: number,
	deepResearchResponseId?: number
): UsePinnedResearchFactsResult => {
	useGetPinnedFactsQuery(prospectId as number, { skip: !prospectId });

	const pinnedFacts = useAppSelector((state) => selectPinnedFacts(state, prospectId));
	const [pinResearchFact, { isLoading: isPinLoading }] = usePinResearchFactMutation();
	const [unpinResearchFact, { isLoading: isUnpinLoading }] = useUnpinResearchFactMutation();

	const pinnedByKey = useMemo(
		() => new Map(pinnedFacts.map((fact) => [fact.factKey, fact])),
		[pinnedFacts]
	);

	const pinnedKeys = useMemo(() => new Set(pinnedByKey.keys()), [pinnedByKey]);

	const togglePin = useCallback(
		async (fact: PinnableFact) => {
			if (!prospectId) {
				return;
			}

			const existing = pinnedByKey.get(fact.factKey);

			if (existing) {
				await unpinResearchFact({ id: existing.id, prospectId }).unwrap();

				return;
			}

			await pinResearchFact({
				prospectId,
				deepResearchResponseId,
				factKey: fact.factKey,
				factLabel: fact.factLabel || undefined,
				factText: fact.factText,
				sourceUrls: fact.sources?.map((source) => source.url),
			}).unwrap();
		},
		[prospectId, deepResearchResponseId, pinnedByKey, pinResearchFact, unpinResearchFact]
	);

	const unpinFact = useCallback(
		async (id: number) => {
			if (!prospectId) {
				return;
			}

			await unpinResearchFact({ id, prospectId }).unwrap();
		},
		[prospectId, unpinResearchFact]
	);

	return {
		pinnedFacts,
		pinnedKeys,
		pinnedCount: pinnedFacts.length,
		isPinning: isPinLoading || isUnpinLoading,
		togglePin,
		unpinFact,
	};
};
